import { createDecorator } from 'vue-class-component';
import { injectAngular } from '@/angular-bridge/injector';

/**
 * Apply to a VueComponent to automatically register it for use within the angular app.
 * Must be annotated after @Component!
 * The directive must be called with the name of the component (in kebab-notation).
 */
export function AngularDirective(bindingName) {
    console.assert(
        typeof bindingName === 'string',
        'AngularDirective must be called with a string argument',
    );
    return component => {
        component.angularDirective = bindingName;
        return component;
    };
}

export function AngularRoute(name, url, { slot = 'default', data = null } = {}) {
    return component => {
        component.angularRoute = { name, url, slot, data };
    };
}

export function ngInject() {
    return createDecorator((options, key) => {
        const mixins = options.mixins || (options.mixins = []);
        mixins.push({
            computed: {
                [key]: function ngInjector() {
                    const result = injectAngular(key);
                    console.assert(result, 'Failed to inject service: ', key);
                    return result;
                },
            },
        });
    });
}

export function makeFungusComponent(Component, template, query) {
    // Important: you cannot use Component.options.mixins because the options are already normalized
    // You have to append to existing hook callbacks instead

    // Parse and defaults for the block's options
    const { tag = 'div' } = query;

    // If a <template> is missing, provide a simple render function.
    if (!Component.options.render) {
        Component.options.render = h => h(tag);
    }

    const appendHook = (hook, callback) => {
        Component.options[hook] = Component.options[hook] || [];
        Component.options[hook].push(callback);
    };

    appendHook('mounted', function fungusMount() {
        const el = angular.element(this.$el);
        el.injector().invoke(
            /* @ngInject */ ($compile, $rootScope) => {
                // Build scope
                const myScope = $rootScope.$new();
                const methodKeys = Object.keys(this).filter(
                    key => key[0] !== '$' && key[0] !== '_',
                );
                const reactiveKeys = Object.keys(Object.getPrototypeOf(this));
                const allKeys = [...reactiveKeys, ...methodKeys];
                const modelProp = this.$options.model ? this.$options.model.prop : null;
                const modelEvent = this.$options.model ? this.$options.model.event : null;

                allKeys.forEach(key => {
                    // Initial value for scope
                    myScope[key] = this[key];

                    // Sync: Vue -> Angular
                    this.$watch(
                        key,
                        value => {
                            if (myScope[key] !== value) {
                                myScope[key] = value;
                            }
                            // Always try to trigger digest, changes may have been from deep watch
                            const inDigestCycle = !!myScope.$$phase;
                            if (!inDigestCycle) {
                                myScope.$apply();
                            }
                        },
                        { deep: true },
                    );

                    // Sync: Angular -> Vue
                    const normalPropChange = value => {
                        this[key] = value;
                    };
                    const modelPropChange = value => {
                        this.$emit(modelEvent, value);
                    };
                    const changeHandler = key === modelProp ? modelPropChange : normalPropChange;
                    myScope.$watch(key, value => {
                        if (value !== this[key]) {
                            changeHandler(value);
                        }
                    });
                });

                // Compile the angular template
                el.append($compile(template)(myScope));
            },
        );
    });
}
